/**
 * 用户管理页面
 * 仅管理员可访问，用于管理系统用户和权限
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Select,
  message,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
} from 'antd';
import {
  UserOutlined,
  CrownOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { getAllUsers, updateUserRole, getUserStats } from '../../services/api';
import { User, UserRole } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const { Title } = Typography;
const { Option } = Select;

interface UserStats {
  total: number;
  admin: number;
  user: number;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats>({ total: 0, admin: 0, user: 0 });
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const { user: currentUser } = useAuth();

  // 加载用户列表
  const loadUsers = async () => {
    try {
      setLoading(true);
      const [usersData, statsData] = await Promise.all([
        getAllUsers(),
        getUserStats()
      ]);
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      message.error('加载用户列表失败');
      console.error('Load users error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // 编辑用户角色
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    form.setFieldsValue({
      role: user.role,
    });
    setEditModalVisible(true);
  };

  // 保存用户角色修改
  const handleSaveRole = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedUser) return;

      await updateUserRole(selectedUser.id, values.role);
      message.success('用户角色更新成功');
      setEditModalVisible(false);
      setSelectedUser(null);
      loadUsers(); // 重新加载用户列表
    } catch (error) {
      message.error('更新用户角色失败');
      console.error('Update user role error:', error);
    }
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditModalVisible(false);
    setSelectedUser(null);
    form.resetFields();
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (username: string, record: User) => (
        <Space>
          <UserOutlined />
          <span>{username}</span>
          {record.id === currentUser?.id && <Tag color="blue">当前用户</Tag>}
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => (
        <Tag
          color={role === 'admin' ? 'red' : 'blue'}
          icon={role === 'admin' ? <CrownOutlined /> : <UserOutlined />}
        >
          {role === 'admin' ? '管理员' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
            disabled={record.id === currentUser?.id} // 不能编辑自己的角色
          >
            编辑角色
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>用户管理</Title>
        <p>管理系统用户和权限设置</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总用户数"
              value={stats.total}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="管理员数量"
              value={stats.admin}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="普通用户数量"
              value={stats.user}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 用户列表 */}
      <Card
        title="用户列表"
        extra={
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={loadUsers}
            loading={loading}
          >
            刷新
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 编辑用户角色对话框 */}
      <Modal
        title={`编辑用户角色 - ${selectedUser?.username}`}
        open={editModalVisible}
        onOk={handleSaveRole}
        onCancel={handleCancelEdit}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="role"
            label="用户角色"
            rules={[{ required: true, message: '请选择用户角色' }]}
          >
            <Select placeholder="请选择用户角色">
              <Option value="user">
                <Space>
                  <UserOutlined />
                  普通用户
                </Space>
              </Option>
              <Option value="admin">
                <Space>
                  <CrownOutlined />
                  管理员
                </Space>
              </Option>
            </Select>
          </Form.Item>
          <div style={{ padding: '12px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
              <strong>角色说明：</strong>
              <br />
              • 普通用户：可以查看数据概览、导入报告、查看报告管理和预警统计
              <br />
              • 管理员：拥有所有权限，包括基础数据管理和用户管理
            </p>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;